import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Switch,
    Route,
    withRouter
} from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment/moment';
import {
    Table,
    TableFilter,
    ViewEditPaymentPage,
    Popup,
    ViewPaymentsPage,
    ViewDownloadStatus,
} from '../../components';
import './PaymentPageListing.scss';
import { PAYMENT_PAGE_TRANSACTION_STATUS } from '../../utils/constants/commonValues';

import { fetchPaymentPageRequest, generateReportRequest } from './redux/paymentPage.actions';
import CreatePaymentPage from './CreatePaymentPage';

import EditPaymentPage from './EditPaymentPage';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import SuccessModal from 'components/PaymentPageTab/SuccessModal';
import DownloadFile from './DownloadFile';

const pageSizeOptions = [10, 20, 40, 60];
const filterFieldList = [
    {
        fieldType: 'date',
        label: 'Date Range',
        name: 'createdOn',
        props: {
            startDate: moment().endOf('day').subtract(7, 'days'), // Start date set to one week ago
            endDate: moment().endOf('day'), // End date set to today
            maximumNights: 30,
            notResetDate: true,
        },
    },
    {
        fieldType: 'input',
        label: 'Page ID',
        name: 'pageId',
        props: {
            placeholder: 'Enter Page ID',
        },
    },
    {
        fieldType: 'radio',
        label: 'Status',
        name: 'status',
        props: {
            options: [
                {
                    name: 'Active',
                    value: 'ACTIVE',
                },
                {
                    name: 'Inactive',
                    value: 'INACTIVE',
                },
                {
                    name: 'Draft',
                    value: 'DRAFT',
                },
            ],
            labelField: 'name',
            valueField: 'value',
        },
    },
];

// Filter List for the transactions view of payment link table.
const filterFieldListView = [
    {
        fieldType: 'date',
        label: '',
        name: 'createdOn',
        props: {
            startDate: moment().endOf('day').subtract(7, 'days'), // Start date set to one week ago
            endDate: moment().endOf('day'), // End date set to today
            maximumNights: 30,
            notResetDate: true,
        },
    },
    {
        fieldType: 'input',
        label: 'Transaction ID',
        name: 'transactionId',
        props: {
            placeholder: 'Enter ID',
        },
    },
    {
        fieldType: 'select',
        label: 'Status',
        name: 'transactionStatus',
        props: {
            data: (function() {
                const options = [];
                for (const key in PAYMENT_PAGE_TRANSACTION_STATUS) {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            PAYMENT_PAGE_TRANSACTION_STATUS,
                            key
                        )
                    ) {
                        const value = PAYMENT_PAGE_TRANSACTION_STATUS[key];
                        options.push({
                            name: value,
                            value: key,
                        });
                    }
                }
                return options;
            })(),
            label: 'Select Status',
            labelField: 'name',
            valueField: 'value',
        },
    },
];

// Payment Link Table Action Popup.
const PaymentPageActions = ({ data, fetchData, resetPage, history }) => {
    const [showFilter, setShowFilter] = useState(false);
    const [actionType, setActionType] = useState(null);

    const handleOpenActionPopup = actionType => {
        setShowFilter(true);
        setActionType(actionType);
    };

    const handleCloseOpenActionPopup = () => {
        setShowFilter(false);
        setActionType(null);
    };

    const handleEditPaymentPage = id => {
        if(data?.original?.status === 'ACTIVE'){
            toast.error("Payment Link already generate, cannot be editied now ");
        }
        else{
            history.push(`/payment-page/edit/${id}`);
        }
    };

    const handleCopy = text => {
        if(text?.status == 'DRAFT'){
            toast.error("Payment Link is in draft state, Link cannot copied.");
        }else{
            let linkUrl = text.linkUrl;
            navigator.clipboard
                .writeText(linkUrl)
                .then(() => {
                    toast.success('Link copied to clipboard');
                })
                .catch(err => {
                    toast.error('Failed to copy link');
                });
        }
    };
    const handleDuplicate = id => {
        history.push(`/payment-page/edit/${id}/new`);
    }

    return (
        <div className="userAction multiItem">
            {(data?.original?.status === 'ACTIVE' || data?.original?.status === 'INACTIVE') && (
                <button
                className="view" title="View"
                onClick={() => handleOpenActionPopup('view')}
            />
            )}
            {data?.original?.status === 'DRAFT' && (
                <button
                className="edit" title="Edit"
                onClick={() => handleEditPaymentPage(data.original.pageId)}
            />
            )}
            {(data?.original?.status === 'ACTIVE') && (
                <button
                className="duplicate" title="Clone"
                onClick={() => handleDuplicate(data.original.pageId)}
            />
            )}
            {data?.original?.status === 'ACTIVE' && (
               <button
                className={`copy`} title="Copy Link"
                onClick={() => handleCopy(data.original)}
           />
            )}
            <Popup
                title={
                    actionType === 'share'
                        ? 'SHARE LINK'
                        : data.original.linkName
                }
                actionType={actionType}
                showCloseIcon
                showOverlay
                showFilter={showFilter}
                data={data.original}
                closePopup={handleCloseOpenActionPopup}
            >
                <ViewEditPaymentPage
                    actionType={actionType}
                    data={data.original}
                    fetchData={fetchData}
                    resetPage={resetPage}
                    initialValues={data.original}
                    handleOpenActionPopup={handleOpenActionPopup}
                    closePopup={handleCloseOpenActionPopup}
                />
            </Popup>
        </div>
    );
};

const PaymentPage = props => {
    const {
        history,
        actions,
        loader: { showLoader },
        paymentPageData = {},
    } = props;

    const {
        tableData,
        isLoading,
        error,
        pagination,
        generatedFile,
        activeReports,
        tableDataTxn,
    } = paymentPageData;

    const [filterData, setFilterData] = useState({});
    const [pageCount, setPageCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isResetPage, setIsResetPage] = useState(false);
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);
    const [showFilter, setShowFilter] = useState(false);

    const handleCreatePaymentpage = () => {
        history.push('/payment-page/create/payment-page');
    };
    const location = useLocation();
    const [isSuccess, setIsSuccess] = useState(false);
    const [linkData, setLinkData] = useState(null);
    const columns = useMemo(
        () => [
            { header: 'Page ID', accessor: 'pageId' },
            { header: 'Merchant Name', accessor: 'entityName' },
            {
                header: 'Amount ',
                accessor: 'amount',
            },
            { header: 'Created On', accessor: 'createdOn'},
            { header: 'Valid Till', accessor: 'validTill' },
            { header: 'Status', accessor: 'status' },
            { header: 'Action', accessor: 'action' },
        ],
        []
    );

    // Fetching table data function.
    const fetchData = useCallback(
        ({ pageSize, pageIndex }) => {
            // This will get called when the table needs new data
            // You could fetch your data from literally anywhere,
            const data = {
                pageSize,
                pageIndex,
            };
            actions.fetchPaymentPageRequest({ ...data, ...filterData });
            setPageSize(pageSize);
        },
        [actions, filterData]
    );

    useEffect(() => {
        if (location.state?.showSuccessModal) {
            setIsSuccess(true);
            setLinkData(location.state.successData);
            window.history.replaceState({}, document.title); // Remove state from history
        }
    }, [location]);

    // Handle Pagination for the table
    useEffect(() => {
        setPageCount(Math.ceil(pagination?.totalNumberOfRecords / pageSize));
    }, [pageSize, pagination.totalNumberOfRecords]);

    const [filterDataView, setFilterDataView] = useState({});

    const formatAmount = (value) => {
        if (!value) return "N/A";
        return parseFloat(value).toFixed(value % 1 === 0 ? 0 : 2); // Remove commas, keep decimal if needed
    };

    const transformedData = tableData?.map(item => ({
        ...item,
        amount: (() => {
            if (!item.amountType) return 'N/A';
            switch (item.amountType) {
                case "USER_INPUT":
                    return "-";
                case "FIXED":
                    return formatAmount(item.maxAmount);
                case "VARIABLE":
                    return item.minAmount && item.maxAmount
                        ? `${formatAmount(item.minAmount)} - ${formatAmount(item.maxAmount)}`
                        : "N/A";
                default:
                    return "N/A";
            }
        })()
    }));

    const handleViewDownloads = () => {
        setShowFilter(true);
    };

    const handleCloseViewDownloads = () => {
        setShowFilter(false);
    };

    return (
        <div className="mob-wrapper">
            {isSuccess && (
                <SuccessModal
                    formLinkData={linkData}
                    onClose={() => setIsSuccess(false)}
                />
            )}
            <Switch>
                <Route exact path="/payment-page">
                    <Table
                        id="tid-edit"
                        tableTitle="Payment Page"
                        filterActionTitle="Create Payment Page"
                        columns={columns}
                        isFiltersEnable
                        isPaginationEnable
                        data={transformedData || []}
                        filterData={filterData}
                        error={error && error.errorMessage}
                        loading={showLoader || isLoading}
                        roleStatus={'roleStatus'}
                        pageCount={pageCount}
                        fetchData={fetchData}
                        numberOfRecord= { pagination && pagination.totalNumberOfRecords}
                        pageSizeOptions={pageSizeOptions}
                        paginationLabel="Rows per page"
                        onClickTableAction={handleCreatePaymentpage}
                        isResetPage={isResetPage}
                        afterResetPage={() => setIsResetPage(false)}
                        userActions={data => (
                            <PaymentPageActions
                                data={data}
                                fetchData={fetchData}
                                resetPage={() => setIsResetPage(true)}
                                history={history}
                            />
                        )}
                        pagePerView={3}
                        tableFilters={
                            <TableFilter
                                customFilterClass="rightSkewed"
                                showOverlay
                                fieldsList={filterFieldList}
                                onSubmit={filterValues => {
                                    setFilterData(filterValues);
                                    setIsResetPage(true);
                                }}
                                onReset={resetValue => {
                                    setFilterData(resetValue);
                                    setIsResetPage(true);
                                }}
                            />
                        }
                    />
                </Route>
                <Route path="/payment-page/create/payment-page">
                    <CreatePaymentPage history={history} />
                </Route>
                <Route exact path="/payment-page/:linkDataId">
                    <ViewPaymentsPage
                        tableActionPopup={
                            <React.Fragment>
                                <Popup
                                    title="Download Status"
                                    showCloseIcon
                                    showOverlay
                                    showFilter={showFilter}
                                    closePopup={handleCloseViewDownloads}
                                >
                                    <ViewDownloadStatus data={activeReports.reports} />
                                </Popup>
                            </React.Fragment>
                        }
                        additionalActions={() => (
                            <div className="button-container payment-page">
                                <DownloadFile
                                    filterData={filterDataView}
                                    tableData={tableDataTxn}
                                    actions={actions}
                                    showDownloadPopup={showDownloadPopup}
                                    setShowDownloadPopup={setShowDownloadPopup}
                                    generateData={generatedFile}
                                    handleViewDownloads={handleViewDownloads}
                                />

                                <button
                                    className="custom-btn"
                                    id="next-btn"
                                    type="button"
                                    onClick={handleViewDownloads}
                                >
                                    VIEW DOWNLOADS
                                </button>
                            </div>
                        )}
                        filterData={filterDataView}
                        tableFilters={
                            <TableFilter
                                customFilterClass="rightSkewed"
                                showOverlay
                                fieldsList={filterFieldListView}
                                onSubmit={filterValues => {
                                    setFilterDataView(filterValues);
                                    setIsResetPage(true);
                                }}
                                onReset={resetValue => {
                                    setFilterDataView(resetValue);
                                    setIsResetPage(true);
                                }}
                            />
                        }
                    />
                </Route>
                <Route path="/payment-page/edit/:formId">
                    <EditPaymentPage paymentPageData={paymentPageData} />
                </Route>
            </Switch>
        </div>
    );
};

const mapStateToProps = ({ form, loader, editData, paymentPageData }) => ({
    form,
    loader,
    editData,
    paymentPageData,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchPaymentPageRequest,
            generateReportRequest,
        },
        dispatch
    ),
});

PaymentPageActions.propTypes = {
    data: PropTypes.object,
    fetchData: PropTypes.func,
    resetPage: PropTypes.func,
    history: PropTypes.object,
};

PaymentPage.propTypes = {
    actions: PropTypes.object,
    history: PropTypes.object,
    paymentPageData: PropTypes.object,
    loader: PropTypes.object,
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(PaymentPage)
);
